import { createStore } from '@/lib/tinyStore';
import { presenceActions } from '@/features/presence/presenceStore';
/* Deep import, not the feature barrel: the barrel pulls in the calls PAGES, and
   the popup host lives in the always-loaded shell chunk. */
import { activeCallActions } from '../store/activeCallStore';
import { callsApi } from './callsClient';
import type { DeclineReason, IncomingCall, IncomingCallState } from './types';
import { dismissDesktopNotificationsForScope } from '@/features/notifications/desktopNotifications';
/* Deep import for the same reason as the two above: the notifications barrel
   pulls in the Activity PAGE, and this module lives in the always-loaded
   shell chunk. */
import { notificationActions } from '@/features/notifications/notificationStore';

export const callPopupStore = createStore<IncomingCallState>({ calls: [], settling: [] });

/** Presence follows the call state — one place, so it can never drift. */
function syncPresence(): void {
  presenceActions.setFlags({ inCall: callPopupStore.get().calls.length > 0 });
}

export const callPopupActions = {
  ring(call: IncomingCall): void {
    callPopupStore.set((state) =>
      state.calls.some((existing) => existing.callId === call.callId)
        ? state
        : { ...state, calls: [call, ...state.calls] },
    );
  },

  /** Caller hung up, another device answered, or the ring timeout elapsed. */
  cancel(callId: string): void {
    callPopupStore.set((state) => {
      const calls = state.calls.filter((call) => call.callId !== callId);
      if (calls.length === state.calls.length) return state;
      return { calls, settling: state.settling.filter((id) => id !== callId) };
    });
    /* EVERY ring leaves through here — answered, declined, cancelled by the
       caller, or timed out. Reconciling once at the single exit is what makes a
       stuck flag impossible, rather than remembering to clear it at four call
       sites and missing one, which is exactly what happened. */
    syncPresence();
    /* And take the OS banner back. Call notifications are raised with
       `requireInteraction`, so the system will NOT retire them by itself —
       without this they outlive the call and reappear every time the
       notification centre is opened. */
    dismissDesktopNotificationsForScope(callId);
    /* ...and the ACTIVITY ROW, which is a different surface and was the one
       nobody was clearing. The server retires it too (services/notifyCalls.ts)
       and its `notification.dismissed` is the authority; doing it here as well
       means the badge falls the instant the popup does rather than one network
       round trip later. Idempotent: whichever arrives first, the second is a
       no-op. */
    notificationActions.withdraw([], `call:${callId}`);
  },

  /** Drops calls whose server-side ring window has closed. */
  sweep(now = Date.now()): void {
    const state = callPopupStore.get();
    if (state.calls.length === 0) return;
    const calls = state.calls.filter((call) => call.ringExpiresAt > now);
    if (calls.length !== state.calls.length) callPopupStore.set({ ...state, calls });
  },

  /**
   * Answer, and hand the minted grant to the call room.
   *
   * The grant is single-use — `answer` only moves a RINGING participant to
   * connected — so it travels in `activeCallStore` instead of being re-requested
   * after the navigation. Returns the call id to open, or null if it failed.
   */
  async accept(callId: string, withVideo: boolean): Promise<string | null> {
    callPopupStore.set((state) => ({ ...state, settling: [...state.settling, callId] }));
    try {
      const accepted = await callsApi.accept(callId, withVideo);
      activeCallActions.set({
        callId: accepted.callId,
        credentials: accepted.credentials,
        kind: accepted.kind,
        direction: 'incoming',
        peerName: accepted.peerName,
        peerId: accepted.peerId,
        withVideo: accepted.withVideo,
        /* Answering IS the connection — the callee never sees a ringing screen. */
        connected: true,
      });
      /* NO `inCall: true` HERE.
         Answering opens the call in ITS OWN WINDOW, which has its own
         presenceStore. Every clear — end of call, hang-up, unmount, pagehide —
         therefore fires in THAT document and never in this one. Setting the flag
         here left the workspace window in auto-DND permanently after the first
         answered call, and auto-DND silences later rings (useShouldRing), so the
         "in a call" indicator cost you the calls that came after it.
         `cancel` below reconciles the flag against the rings that remain, which
         is the only thing this window can actually observe. Reflecting the call
         itself needs a cross-window bus; until that exists, saying nothing is
         correct and saying "in a call" is a lie this window cannot retract. */
      callPopupActions.cancel(callId);
      return accepted.callId;
    } catch {
      callPopupStore.set((state) => ({
        ...state,
        settling: state.settling.filter((id) => id !== callId),
      }));
      return null;
    }
  },

  async decline(callId: string, reason: DeclineReason = 'declined'): Promise<void> {
    callPopupActions.cancel(callId);
    syncPresence();
    await callsApi.decline(callId, reason).catch(() => undefined);
  },

  async declineWithMessage(callId: string, message: string): Promise<void> {
    callPopupActions.cancel(callId);
    syncPresence();
    await callsApi.declineWithMessage(callId, message).catch(() => undefined);
  },
};

export const selectCalls = (state: IncomingCallState): readonly IncomingCall[] => state.calls;
export const selectSettling = (state: IncomingCallState): readonly string[] => state.settling;
